/* eslint-disable max-lines-per-function, max-params */
import { track } from "jiffy-analytics";
import { api } from "jiffy-api";
import { EventBus, Events } from "jiffy-store";
import { on } from "common/utils/ui/event";
import initSavedCartsEditModalConsumer, {
  provideNewCartsData
} from "../saved-carts-edit-modal/saved-carts-edit-modal-consumer";

import "../saved-carts-edit-modal/saved-carts-edit-modal.scss";

const SELECTORS = {
  CREATE_CART_MODAL: ".js-saved-carts-create-modal",
  CREATE_CART_MODAL_INPUT: ".js-saved-carts-create-modal-input",
  CREATE_CART_MODAL_CLOSE: ".js-saved-carts-create-modal-close",
  CREATE_FORM: ".js-create-modal-form",
  SUBMIT_BUTTON: ".js-create-cart-submit",
  INPUT: ".js-qty-form-saved-carts-input",
  ITEMS: ".js-saved-cart-item",
  CART_NAME_INPUT: "#js-cart-name-control",
  NOTES_INPUT: "#js-notes-control",
  CONSUMER_GUID_INPUT: ".js-saved-carts-consumer-guid-input",
  GET_BY_DATE_INPUT: ".js-saved-carts-get-by-date-input"
};

const PLACEHOLDERS = {
  cartName: "Cart Name",
  notes: "Add some notes"
};

const FORM_CLASSES = {
  invalidInput: "saved-carts-create-modal__input--invalid",
  invalidLabel: "saved-carts-create-modal__label--invalid"
};

const $getDateInput = () => document.querySelector(SELECTORS.GET_BY_DATE_INPUT);

const setMinLocalDate = () => {
  const $input = $getDateInput();
  if (!$input) return;
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  $input.min = `${yyyy}-${mm}-${dd}`;
};

const initGetByDateSelector = () => {
  // Set min date to today input
  setMinLocalDate();
  // open datepicker on input focus
  on("click", ".js-saved-carts-get-by-date-input", e => {
    e.target.showPicker();
  });
};

export default (openButtonSelector, wrapper, trackEvents = null, isAjax = false) => {
  const $createCartBtn = wrapper.querySelector(openButtonSelector);
  const $createForm = document.querySelector(SELECTORS.CREATE_FORM);
  if (!$createCartBtn || !$createForm) return;
  const $createCartModal = $(SELECTORS.CREATE_CART_MODAL);

  $createCartBtn.addEventListener("click", () => {
    $createCartModal.modal("show");
    track("saved_carts_new_cart_modal_opened");
    trackEvents?.();

    const refocus = () => wrapper.querySelector(SELECTORS.INPUT)?.focus();
    $createCartModal.off("hidden.bs.modal").on("hidden.bs.modal", refocus);
  });

  if ($createForm.dataset.inited) {
    return;
  }

  const $createCartInputs = document.querySelectorAll(SELECTORS.CREATE_CART_MODAL_INPUT);
  const $closeCreateCartBtn = document.querySelectorAll(SELECTORS.CREATE_CART_MODAL_CLOSE);
  const $submitButton = document.querySelector(SELECTORS.SUBMIT_BUTTON);
  const $cartNameControl = $createForm.querySelector("#js-cart-name-control");

  const inputHandler = ({ target }) => {
    const $target = target;
    if (!($target instanceof HTMLElement)) return;

    if (!$target.value) {
      $target.classList.add(FORM_CLASSES.invalidInput);
      $target.nextElementSibling.classList.add(FORM_CLASSES.invalidLabel);
    } else {
      $target.classList.remove(FORM_CLASSES.invalidInput);
      $target.nextElementSibling.classList.remove(FORM_CLASSES.invalidLabel);
    }
  };

  ["blur", "input"].forEach(eventName =>
    $cartNameControl.addEventListener(eventName, ({ target }) => {
      inputHandler({ target });
    })
  );

  $createCartModal.on("shown.bs.modal", () => {
    $createCartInputs.forEach(element => {
      const $input = element;

      if ($input.id === $cartNameControl.id) {
        $input.focus();
        $input.placeholder = PLACEHOLDERS.cartName;
        $input.classList.remove(FORM_CLASSES.invalidInput);
        $input.nextElementSibling.classList.remove(FORM_CLASSES.invalidLabel);
        $submitButton.removeAttribute("disabled");
      } else if ($input.id === "notes-control") {
        $input.placeholder = PLACEHOLDERS.notes;
      }
    });
  });

  $createCartInputs.forEach(input => {
    input.addEventListener("blur", () => {
      track(`saved_carts_new_cart_modal_${input.id}_updated`);
    });
  });

  $closeCreateCartBtn.forEach(btn => {
    btn.addEventListener("click", () => {
      $createCartModal.modal("hide");
      $createForm.reset();
      track("saved_carts_new_cart_modal_canceled");
    });
  });

  const getCurrentCartName = () => $createForm.querySelector(SELECTORS.CART_NAME_INPUT)?.value;

  const getCurrentNotes = () => $createForm.querySelector(SELECTORS.NOTES_INPUT)?.value;

  const getCurrentConsumerGuid = () =>
    $createForm.querySelector(SELECTORS.CONSUMER_GUID_INPUT)?.value;

  const getCurrentGetByDate = () => $createForm.querySelector(SELECTORS.GET_BY_DATE_INPUT)?.value;

  const createCartRequest = () => {
    const data = {
      saved_carts: {
        cart_name: getCurrentCartName(),
        notes: getCurrentNotes(),
        consumer_guid: getCurrentConsumerGuid(),
        get_by_date: getCurrentGetByDate()
      }
    };
    return api.account.savedCarts.create(data);
  };

  const trackSavedCartCreated = () => {
    track("saved_carts_new_cart_modal_created", {
      name: getCurrentCartName(),
      created_at: new Date().toISOString()
    });
  };

  $createForm.addEventListener("submit", async e => {
    e.preventDefault();
    e.stopPropagation();

    $submitButton.setAttribute("disabled", "disabled");

    const isFormInvalid = $cartNameControl.classList.contains(FORM_CLASSES.invalidInput);

    if (isFormInvalid) return;

    if (isAjax) {
      const cart = await createCartRequest();
      cart.items = 0;
      cart.amount = "0.00";
      trackSavedCartCreated();
      $createCartModal.modal("hide");
      $createForm.reset();
      provideNewCartsData({ customer: {}, number: null });
      EventBus.publish(Events.SAVED_CART_CREATED, null, cart);
    }

    if (trackEvents) {
      trackSavedCartCreated();
      $createForm.submit();
    }
  });

  $createForm.dataset.inited = "true";

  initGetByDateSelector();

  initSavedCartsEditModalConsumer("#saved-carts-create-modal-consumer", {
    isModalWindow: true,
    inputLabel: "Customer (optional)",
    staticSelectionName: "saved_carts[consumer_guid]"
  });
};
